import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(response.data);
    } catch (error) {
      toast.error("Failed to load settings");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    const token = localStorage.getItem("admin_token");
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/settings/update/${key}`,
        { value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Setting updated successfully");
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update setting");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Site Settings</h1>
        <p className="text-muted-foreground mt-1">Platform configuration and feature flags</p>
      </div>

      {/* Feature Flags */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Feature Flags</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="text-foreground font-medium">Case Review (Pro/Org Feature)</p>
              <p className="text-muted-foreground text-sm">Enable case review functionality for Pro and Org plans</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.feature_flags?.case_review_enabled || false}
                onChange={(e) => updateSetting("feature_flags", {
                  ...settings.feature_flags,
                  case_review_enabled: e.target.checked
                })}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="text-foreground font-medium">OCR Processing</p>
              <p className="text-muted-foreground text-sm">Enable OCR for scanned PDF documents</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.feature_flags?.ocr_enabled || false}
                onChange={(e) => updateSetting("feature_flags", {
                  ...settings.feature_flags,
                  ocr_enabled: e.target.checked
                })}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="text-foreground font-medium">PDF Export</p>
              <p className="text-muted-foreground text-sm">Allow users to export answers as PDF</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.feature_flags?.pdf_export_enabled || false}
                onChange={(e) => updateSetting("feature_flags", {
                  ...settings.feature_flags,
                  pdf_export_enabled: e.target.checked
                })}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="text-foreground font-medium">Email Verification Required</p>
              <p className="text-muted-foreground text-sm">Block premium features for unverified users</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.feature_flags?.email_verification_required || false}
                onChange={(e) => updateSetting("feature_flags", {
                  ...settings.feature_flags,
                  email_verification_required: e.target.checked
                })}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Announcement Banner */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Announcement Banner</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-foreground font-medium">Enable Announcement</label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.announcement?.enabled || false}
                onChange={(e) => updateSetting("announcement", {
                  ...settings.announcement,
                  enabled: e.target.checked
                })}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div>
            <label className="block text-muted-foreground text-sm mb-2">Message</label>
            <textarea
              value={settings?.announcement?.message || ""}
              onChange={(e) => {
                const newSettings = {
                  ...settings,
                  announcement: {
                    ...settings.announcement,
                    message: e.target.value
                  }
                };
                setSettings(newSettings);
              }}
              rows={3}
              className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Enter announcement message..."
            />
          </div>

          <div>
            <label className="block text-muted-foreground text-sm mb-2">Type</label>
            <select
              value={settings?.announcement?.type || "info"}
              onChange={(e) => {
                const newSettings = {
                  ...settings,
                  announcement: {
                    ...settings.announcement,
                    type: e.target.value
                  }
                };
                setSettings(newSettings);
              }}
              className="w-full px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="info">Info (Blue)</option>
              <option value="warning">Warning (Yellow)</option>
              <option value="success">Success (Green)</option>
              <option value="error">Error (Red)</option>
            </select>
          </div>

          <button
            onClick={() => updateSetting("announcement", settings.announcement)}
            disabled={saving}
            className="w-full px-4 py-2 bg-teal-600 hover:bg-primary/90 disabled:opacity-50 text-foreground rounded-lg transition"
          >
            {saving ? "Saving..." : "Save Announcement"}
          </button>
        </div>
      </div>

      {/* Maintenance Mode */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Maintenance Mode</h2>
        <div className="space-y-4">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            <p className="text-red-400 text-sm font-medium">⚠️ Warning: This will make the site unavailable to users</p>
          </div>

          <div className="flex items-center justify-between">
            <label className="text-foreground font-medium">Enable Maintenance Mode</label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.maintenance_mode?.enabled || false}
                onChange={(e) => updateSetting("maintenance_mode", {
                  ...settings.maintenance_mode,
                  enabled: e.target.checked
                })}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-500"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
